import { CheckCircle2Icon, CircleCheckIcon, CircleDotIcon, CircleIcon, LoaderCircleIcon } from "lucide-react";
import { Chip } from "@/components/badge/chip";
import { UserChip } from "@/components/user-chip";
import { useUserMap } from "@/hooks/users";
import { cn } from "@/lib/utils";
import type { Engagement, PersonalStatus } from "@/types/domain";

export const STATUS_LABEL: Record<PersonalStatus, string> = { open: "Not Started", in_progress: "In Process", done: "Done" };
export const STATUS_TONE = { open: "neutral", in_progress: "yellow", done: "green" } as const satisfies Record<PersonalStatus, string>;
export const STATUS_ICON: Record<PersonalStatus, typeof CircleDotIcon> = { open: CircleDotIcon, in_progress: LoaderCircleIcon, done: CheckCircle2Icon };

// Current-status icon; In Process spins (respects reduced motion).
export const StatusIcon = ({ status, className }: { status: PersonalStatus; className?: string }) => {
  const Icon = STATUS_ICON[status];
  return (
    <Icon
      aria-hidden="true"
      className={cn(status === "in_progress" && "motion-safe:animate-spin", className)}
    />
  );
};

// Row-leading icon for my own progress (wish-pool Me view). Same vocabulary
// as CompletionProgress: empty circle = not started, green check = done;
// In Process spins because it's mine to move.
const MY_STATUS_ICON: Record<PersonalStatus, [typeof CircleIcon, string]> = {
  open: [CircleIcon, "text-muted-foreground"],
  in_progress: [LoaderCircleIcon, "text-chip-yellow-foreground"],
  done: [CircleCheckIcon, "text-chip-green-foreground"],
};

export const MyStatusIcon = ({ status }: { status: PersonalStatus }) => {
  const label = `我的进度：${STATUS_LABEL[status]}`;
  const [Icon, tone] = MY_STATUS_ICON[status];
  return (
    <span className={cn("flex shrink-0 items-center", tone)} title={label} aria-label={label}>
      <Icon
        aria-hidden="true"
        className={cn("h-4 w-4", status === "in_progress" && "motion-safe:animate-spin")}
      />
    </span>
  );
};

// The service derives this roster from the same records as personal Done and Inbox.
// A person holding multiple roles appears once, scoped to this App.
// `spin`: version lists spin the in-process icon (primary) so active rows
// catch the eye; elsewhere (wish-pool All, inbox) it's a static dot.
export const CompletionProgress = ({ engagement, spin = false }: { engagement: Pick<Engagement, "completionParticipants">; spin?: boolean }) => {
  const users = useUserMap();
  const participants = engagement.completionParticipants ?? [];
  if (!participants.length) {
    return <span className="text-xs text-muted-foreground" title="No participants">—</span>;
  }
  const pending = participants.filter((participant) => !participant.completedAt);
  const done = participants.length - pending.length;
  const started = participants.some((participant) => participant.status !== "open");
  const label = `${done}/${participants.length} completed`;
  // Overall progress, icon only: all done = green check, in process
  // (someone started or finished, not everyone done) = dotted circle, or a
  // spinning loader when `spin`; nobody started = empty circle. Counts stay
  // in the tooltip.
  const [Icon, tone] = !pending.length
    ? [CircleCheckIcon, "text-chip-green-foreground"]
    : started
    ? spin ? [LoaderCircleIcon, "text-primary"] : [CircleDotIcon, "text-chip-yellow-foreground"]
    : [CircleIcon, "text-muted-foreground"];
  return (
    <span
      className={cn("flex shrink-0 items-center", tone)}
      aria-label={label}
      title={pending.length
        ? `${label} — pending: ${pending.map(({ userId }) => users.get(userId)?.name ?? userId).join(", ")}`
        : "All completed"}
    >
      <Icon aria-hidden="true" className={cn("h-4 w-4", Icon === LoaderCircleIcon && "motion-safe:animate-spin")} />
    </span>
  );
};

export const CompletionParticipants = ({ engagement }: { engagement: Engagement }) => {
  const users = useUserMap();
  const participants = engagement.completionParticipants ?? [];
  if (!participants.length) {
    return <p className="text-sm text-muted-foreground">No participants.</p>;
  }
  return (
    <ul className="flex flex-col gap-2">
      {participants.map(({ userId, status }) => (
        <li key={userId} className="flex items-center justify-between gap-3">
          <UserChip user={users.get(userId) ?? { id: userId, name: userId }} />
          <Chip tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Chip>
        </li>
      ))}
    </ul>
  );
};
